import React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { StepView } from "../types";

export type FlowStepState = "done" | "skipped" | "current" | "upcoming";

/** As the merchant's StepsCard reads a step: settled steps are done, a skipped one greyed. */
export function flowStepState(step: StepView, currentKey: string | null): FlowStepState {
  if (step.key === currentKey) return "current";
  if (step.skipped) return "skipped";
  if (step.merchant_complete || step.bypassed) return "done";
  return "upcoming";
}

/** The steps the merchant sees: an abort-only or repeated step only while it is the current one. */
export function visibleSteps(steps: StepView[], currentKey: string | null): StepView[] {
  return steps.filter((step) => step.key === currentKey || !(step.withheld || step.omitted));
}

const STATE_LABELS: Record<FlowStepState, string> = {
  done: "done",
  skipped: "skipped",
  current: "current step",
  upcoming: "not started",
};

function Marker({ state }: { state: FlowStepState }) {
  return (
    <span
      aria-hidden
      className={cn(
        "relative z-10 flex size-4 shrink-0 items-center justify-center rounded-full border",
        state === "done" && "border-primary bg-primary text-primary-foreground",
        state === "skipped" && "border-muted-foreground/30 bg-muted text-muted-foreground/60",
        state === "current" && "border-2 border-primary bg-background ring-2 ring-primary/20",
        state === "upcoming" && "border-muted-foreground/40 bg-background"
      )}
    >
      {state === "done" || state === "skipped" ? (
        <Check className="size-2.5" strokeWidth={3} />
      ) : null}
    </span>
  );
}

interface FlowStepperProps {
  steps: StepView[];
  /** The step the merchant is on; null before they start. */
  currentKey: string | null;
  className?: string;
}

/** The flow's steps as a chained list: one marker per step, joined by a line. */
const FlowStepper: React.FC<FlowStepperProps> = ({ steps, currentKey, className }) => (
  <ol aria-label="Steps" className={cn("flex flex-col", className)}>
    {visibleSteps(steps, currentKey).map((step, index, shown) => {
      const state = flowStepState(step, currentKey);
      return (
        <li
          key={step.key}
          data-state={state}
          aria-current={state === "current" ? "step" : undefined}
          className="relative flex min-h-6 items-start gap-2 pb-2 last:pb-0"
        >
          {index < shown.length - 1 ? (
            <span aria-hidden className="absolute bottom-0 left-[7.5px] top-4 w-px bg-border" />
          ) : null}
          <Marker state={state} />
          <span
            className={cn(
              "min-w-0 text-xs leading-4 [overflow-wrap:anywhere]",
              state === "current" && "font-medium text-foreground",
              state === "done" && "text-foreground",
              (state === "skipped" || state === "upcoming") && "text-muted-foreground"
            )}
          >
            {step.label}
            <span className="sr-only">, {STATE_LABELS[state]}</span>
          </span>
        </li>
      );
    })}
  </ol>
);

export default FlowStepper;
